// src/components/AblyDebugPanel.tsx
import {
  ActionIcon,
  Badge,
  Button,
  Card,
  Collapse,
  Divider,
  Group,
  Image,
  ScrollArea,
  Select,
  Stack,
  Switch,
  Tabs,
  Text,
  Title,
  Tooltip,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { ChevronDown, ChevronUp, Radio } from 'lucide-react';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useAbly } from '../providers/AblyProvider';
import { useAuthUser } from '../providers/AuthProvider';

import { DEVICE_IDLE_MS, SENSOR_IDLE_MS } from '@types';
import { foodOptions } from '../data/foods';
import { QuickConfigTab } from '../features/dev-tools';
import { setDebugEnabled, useDebugEnabled } from '../utils/debugFlag';
import { getDeviceImageSrc } from '../utils/deviceImage';
import { createSessionEventManager } from '../utils/sessionEventManager';
import { useUserDevices } from '../hooks/useUserDevices';
import { useDeviceConnectionsStatus } from '../hooks/useDeviceConnectionsStatus';
import { useParams } from 'react-router-dom';
import { useSession } from '../hooks/useSession';

type IntervalMap = Map<string, NodeJS.Timeout>;

const PANEL_WIDTH = 620;
const PANEL_MAX_HEIGHT = '88vh';
const CONTENT_SCROLL_HEIGHT = 520;
const HR_SIM_INTERVAL_MS = SENSOR_IDLE_MS / 2;
const VR_SIM_INTERVAL_MS = DEVICE_IDLE_MS / 2;

const NOOP_EVENT_MANAGER = {
  init: () => {},
  start: () => {},
  stop: () => {},
  cancel: () => {},
  change: () => {},
  customEvent: () => {},
};

const EventPulseIcon: React.FC<{ tick: number; active: boolean }> = ({
  tick,
  active,
}) => (
  <Tooltip label={tick > 0 ? `Evento #${tick}` : 'Nessun evento inviato'}>
    <span
      key={tick}
      className={tick > 0 ? 'ably-debug-event-pulse' : undefined}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        color: active
          ? 'var(--mantine-color-teal-6)'
          : 'var(--mantine-color-gray-5)',
      }}
    >
      <Radio size={14} />
    </span>
  </Tooltip>
);

const AblyDebugPanel: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const debugEnabled = useDebugEnabled();
  const [logEnabled, setLogEnabled] = useState<boolean>(debugEnabled);
  const [exposureFoodId, setExposureFoodId] = useState<string>(
    foodOptions[0]?.value ?? 'pasta'
  );
  const [simulatingIds, setSimulatingIds] = useState<Record<string, boolean>>(
    {}
  );
  const [pulseTicks, setPulseTicks] = useState<Record<string, number>>({});

  const { id } = useParams();
  const user = useAuthUser();
  const {
    connectionStatus,
    sendMessage,
    sentEvents = [],
    clearSentEvents = () => {},
  } = useAbly();
  const { session: currentExperience } = useSession(id);
  const deviceCode = currentExperience?.deviceCode;
  const scenario = currentExperience?.scenario;
  const isExposureSession = currentExperience?.type === 'exposure';

  const defaultTab = isExposureSession ? 'exposure' : 'log';

  const { devices, refetch, error } = useUserDevices(user?.id?.toString());
  const { clearAllTimers } = useDeviceConnectionsStatus(devices);

  const eventManager = useMemo(() => {
    if (!currentExperience) {
      return NOOP_EVENT_MANAGER;
    }
    return createSessionEventManager(
      currentExperience.type || 'default',
      String(currentExperience.id ?? ''),
      currentExperience,
      sendMessage
    );
  }, [currentExperience, sendMessage]);

  const resolveRuntimeId = (
    dev?: { clientId?: string; code?: string } | null
  ) => (dev?.clientId || dev?.code || '').trim();

  const intervalsRef = useRef<IntervalMap>(new Map());

  const stopSimulation = (runtimeId: string) => {
    const existing = intervalsRef.current.get(runtimeId);
    if (existing) {
      clearInterval(existing);
      intervalsRef.current.delete(runtimeId);
    }
  };

  const stopAll = () => {
    intervalsRef.current.forEach(t => clearInterval(t));
    intervalsRef.current.clear();
  };

  useEffect(() => {
    return () => stopAll();
  }, []);

  const ensureUser = () => {
    if (!user?.id) {
      notifications.show({
        color: 'red',
        title: 'Errore',
        message: 'Utente non autenticato',
      });
      return false;
    }
    return true;
  };

  const bumpPulse = (runtimeId: string) => {
    setPulseTicks(prev => ({
      ...prev,
      [runtimeId]: (prev[runtimeId] ?? 0) + 1,
    }));
  };

  const simulateHRHeartRateEventRaw = (runtimeId: string) => {
    const payloadHeartRate = {
      name: 'heartRate',
      clientId: runtimeId,
      data: {
        heartRate: Math.floor(Math.random() * 41) + 60,
        code: runtimeId,
        type: 'private_msg',
      },
    };
    eventManager.customEvent('heartRate', payloadHeartRate);
    bumpPulse(runtimeId);
  };

  const simulateVREventRaw = (runtimeId: string, notify = true) => {
    const payload = {
      name: 'Connected',
      clientId: runtimeId,
      action: 'message.create',
      data: JSON.stringify({ deviceCode: null }),
      connectionId: `MRP2KWyu7T-${Date.now()}`,
      timestamp: Date.now(),
      id: `MRP2KWyu7T:${Math.floor(Math.random() * 1000)}:0`,
      createdAt: Date.now(),
    };
    eventManager.customEvent('vr-event', payload);
    bumpPulse(runtimeId);
    if (notify) {
      notifications.show({
        color: 'green',
        title: 'Evento VR inviato',
        message: `Simulazione evento VR "Connected" da ${runtimeId}`,
      });
    }
  };

  const startHrSimulation = (runtimeId: string) => {
    stopSimulation(runtimeId);
    simulateHRHeartRateEventRaw(runtimeId);
    const intervalId = setInterval(() => {
      simulateHRHeartRateEventRaw(runtimeId);
    }, HR_SIM_INTERVAL_MS);
    intervalsRef.current.set(runtimeId, intervalId);
  };

  const startVrSimulation = (runtimeId: string) => {
    stopSimulation(runtimeId);
    simulateVREventRaw(runtimeId, true);
    const intervalId = setInterval(() => {
      simulateVREventRaw(runtimeId, false);
    }, VR_SIM_INTERVAL_MS);
    intervalsRef.current.set(runtimeId, intervalId);
  };

  const toggleSimulation = (
    runtimeId: string,
    kind: 'hr' | 'vr',
    on: boolean
  ) => {
    if (!runtimeId) return;
    if (on) {
      if (!ensureUser()) return;
      if (kind === 'hr') startHrSimulation(runtimeId);
      else startVrSimulation(runtimeId);
      setSimulatingIds(prev => ({ ...prev, [runtimeId]: true }));
    } else {
      stopSimulation(runtimeId);
      setSimulatingIds(prev => {
        const next = { ...prev };
        delete next[runtimeId];
        return next;
      });
    }
  };

  const resetSimulations = () => {
    stopAll();
    clearAllTimers();
    setSimulatingIds({});
    setPulseTicks({});
  };

  useEffect(() => {
    setLogEnabled(debugEnabled);
  }, [debugEnabled]);

  if (!debugEnabled) return null;

  return (
    <div
      style={{
        position: 'fixed',
        top: 10,
        right: 0,
        zIndex: 1000,
        pointerEvents: 'none',
        width: PANEL_WIDTH,
        maxWidth: '58vw',
      }}
    >
      <Card
        shadow='lg'
        withBorder
        p='md'
        style={{
          margin: '0 16px 16px 16px',
          pointerEvents: 'auto',
          backgroundColor: 'rgba(255, 255, 255, 0.97)',
          backdropFilter: 'blur(10px)',
          maxHeight: PANEL_MAX_HEIGHT,
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <Group gap='sm' align='center'>
          <Title order={5} style={{ margin: 0 }}>
            Developer Panel
          </Title>
          <Badge
            size='sm'
            variant='light'
            color={connectionStatus === 'connected' ? 'green' : 'gray'}
          >
            {connectionStatus}
          </Badge>
          <ActionIcon
            size='sm'
            variant='light'
            onClick={() => setIsOpen(open => !open)}
            style={{ marginLeft: 'auto' }}
          >
            {isOpen ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
          </ActionIcon>
        </Group>

        <Collapse in={isOpen} transitionDuration={100}>
          <Group gap='sm' my='md' justify='space-between'>
            <Tooltip
              label={`Ably logging ${logEnabled ? 'ENABLED' : 'DISABLED'}`}
            >
              <Switch
                size='xs'
                checked={logEnabled}
                onChange={e => {
                  const on = e.currentTarget.checked;
                  setDebugEnabled(on);
                  setLogEnabled(on);
                  console.info(
                    '🔧 ABLY DEBUG LOG →',
                    on ? 'ENABLED' : 'DISABLED'
                  );
                }}
                label='Ably message logging'
              />
            </Tooltip>

            <Group gap='xs'>
              <Button size='xs' variant='light' onClick={() => refetch()}>
                Ricarica
              </Button>
              <Button size='xs' variant='light' onClick={resetSimulations}>
                Reset Store/Timers
              </Button>
            </Group>
          </Group>

          <Stack gap={4} mb='sm'>
            <Text size='xs'>User: {user?.id || 'N/A'}</Text>
            <Text size='xs'>Device Code: {deviceCode ?? '—'}</Text>
            <Text size='xs'>Scenario: {scenario ?? '—'}</Text>
            {error && (
              <Text size='xs' c='red'>
                Errore: {error instanceof Error ? error.message : String(error)}
              </Text>
            )}
          </Stack>

          <Divider my='sm' />

          <ScrollArea
            h={CONTENT_SCROLL_HEIGHT}
            type='auto'
            offsetScrollbars
            style={{ flex: 1 }}
          >
            <Tabs defaultValue={defaultTab} keepMounted={false}>
              <Tabs.List>
                <Tabs.Tab value='quick-config'>Config rapida</Tabs.Tab>
                <Tabs.Tab value='log'>
                  Log{sentEvents.length > 0 ? ` (${sentEvents.length})` : ''}
                </Tabs.Tab>
                <Tabs.Tab value='devices'>Dispositivi</Tabs.Tab>
                {isExposureSession && (
                  <Tabs.Tab value='exposure'>Exposure</Tabs.Tab>
                )}
              </Tabs.List>

              <Tabs.Panel value='quick-config' pt='md'>
                <Card withBorder p='sm'>
                  <Text size='sm' fw={600} mb='sm'>
                    Config rapida
                  </Text>
                  <QuickConfigTab patientId={currentExperience?.patient_id} />
                </Card>
              </Tabs.Panel>

              <Tabs.Panel value='log' pt='md'>
                <Card withBorder p='sm'>
                  <Group justify='space-between' mb='sm'>
                    <Text size='sm' fw={600}>
                      Eventi Ably inviati
                    </Text>
                    <Button size='xs' variant='light' onClick={clearSentEvents}>
                      Pulisci
                    </Button>
                  </Group>
                  <Stack gap='sm'>
                    {[...sentEvents].reverse().map((entry, idx) => (
                      <Card
                        key={`${entry.ts}-${idx}`}
                        withBorder
                        p='sm'
                        radius='sm'
                      >
                        <Group gap='xs' wrap='nowrap' align='flex-start'>
                          <Text
                            size='xs'
                            c='dimmed'
                            style={{ whiteSpace: 'nowrap' }}
                          >
                            {new Date(entry.ts).toLocaleTimeString('it-IT', {
                              hour: '2-digit',
                              minute: '2-digit',
                              second: '2-digit',
                            })}
                          </Text>
                          <Badge size='sm' variant='light'>
                            {entry.event}
                          </Badge>
                        </Group>
                        <pre
                          style={{
                            margin: 0,
                            marginTop: 6,
                            fontSize: 11,
                            overflow: 'auto',
                            maxHeight: 140,
                          }}
                        >
                          {JSON.stringify(entry.data, null, 2)}
                        </pre>
                      </Card>
                    ))}
                    {sentEvents.length === 0 && (
                      <Text size='sm' c='dimmed'>
                        Nessun evento inviato
                      </Text>
                    )}
                  </Stack>
                </Card>
              </Tabs.Panel>

              <Tabs.Panel value='devices' pt='md'>
                <Stack gap='sm'>
                  {devices.length === 0 ? (
                    <Text size='sm' c='dimmed'>
                      Nessun device collegato
                    </Text>
                  ) : (
                    devices.map(d => {
                      const runtimeId = resolveRuntimeId(d);
                      const isSimulating = Boolean(
                        runtimeId && simulatingIds[runtimeId]
                      );
                      return (
                        <Card
                          key={`${d.id}`}
                          withBorder
                          p='sm'
                          style={{ backgroundColor: '#f8f9fa' }}
                        >
                          <Group
                            gap='sm'
                            align='center'
                            wrap='nowrap'
                            justify='space-between'
                          >
                            <Group gap='sm' align='center' wrap='wrap'>
                              <EventPulseIcon
                                tick={
                                  runtimeId ? (pulseTicks[runtimeId] ?? 0) : 0
                                }
                                active={isSimulating}
                              />
                              <Image
                                src={getDeviceImageSrc(d)}
                                h={28}
                                w={28}
                                alt={d.type}
                                fit='contain'
                              />
                              <Text size='sm' fw={500}>
                                {d.name || d.model || `Device #${d.id}`}
                                {runtimeId ? ` (${runtimeId})` : ''}
                              </Text>
                            </Group>
                            {d.type === 'vr-headset' && (
                              <Switch
                                size='xs'
                                label='Simula VR'
                                checked={isSimulating}
                                disabled={!runtimeId}
                                onChange={e =>
                                  toggleSimulation(
                                    runtimeId,
                                    'vr',
                                    e.currentTarget.checked
                                  )
                                }
                              />
                            )}
                            {d.type === 'smartwatch' && (
                              <Switch
                                size='xs'
                                label='Simula HR'
                                checked={isSimulating}
                                disabled={!runtimeId}
                                onChange={e =>
                                  toggleSimulation(
                                    runtimeId,
                                    'hr',
                                    e.currentTarget.checked
                                  )
                                }
                              />
                            )}
                          </Group>
                        </Card>
                      );
                    })
                  )}
                </Stack>
              </Tabs.Panel>

              {isExposureSession && (
                <Tabs.Panel value='exposure' pt='md'>
                  <Card withBorder p='sm'>
                    <Text size='sm' fw={600} mb='sm'>
                      Exposure Test
                    </Text>
                    <Stack gap='sm'>
                      <Group gap='xs' wrap='wrap'>
                        <Button
                          size='sm'
                          variant='light'
                          onClick={() => {
                            const payload = {
                              deviceCode: deviceCode ?? '',
                              sessionId: String(currentExperience?.id ?? ''),
                              timestamp: new Date().toISOString(),
                            };
                            sendMessage('tutorialCompleted', payload);
                            notifications.show({
                              color: 'green',
                              title: 'Evento inviato',
                              message: 'tutorialCompleted',
                            });
                          }}
                          disabled={!deviceCode}
                        >
                          Tutorial completato
                        </Button>
                        <Button
                          size='sm'
                          variant='light'
                          onClick={() => {
                            const payload = {
                              deviceCode: deviceCode ?? '',
                              sessionId: String(currentExperience?.id ?? ''),
                              timestamp: new Date().toISOString(),
                              itemIds: parseInt(exposureFoodId, 10) || 0,
                            };
                            sendMessage('itemShown', payload);
                            notifications.show({
                              color: 'green',
                              title: 'Evento inviato',
                              message: 'itemShown',
                            });
                          }}
                          disabled={!deviceCode}
                        >
                          Cibo mostrato
                        </Button>
                        <Button
                          size='sm'
                          variant='light'
                          onClick={() => {
                            const payload = {
                              deviceCode: deviceCode ?? '',
                              sessionId: String(currentExperience?.id ?? ''),
                              timestamp: new Date().toISOString(),
                              itemIds: parseInt(exposureFoodId, 10) || 0,
                              score: Math.floor(Math.random() * 5) + 1,
                            };
                            sendMessage('itemFeedback', payload);
                            notifications.show({
                              color: 'green',
                              title: 'Evento inviato',
                              message: 'itemFeedback',
                            });
                          }}
                          disabled={!deviceCode}
                        >
                          Feedback cibo
                        </Button>
                        <Button
                          size='sm'
                          variant='light'
                          onClick={() => {
                            const payload = {
                              deviceCode: deviceCode ?? '',
                              sessionId: String(currentExperience?.id ?? ''),
                              timestamp: new Date().toISOString(),
                            };
                            sendMessage('sessionStartedAt', payload);
                            notifications.show({
                              color: 'green',
                              title: 'Evento inviato',
                              message: 'sessionStartedAt',
                            });
                          }}
                          disabled={!deviceCode}
                        >
                          Start sessione
                        </Button>
                        <Button
                          size='sm'
                          variant='light'
                          onClick={() => {
                            const payload = {
                              deviceCode: deviceCode ?? '',
                              sessionId: String(currentExperience?.id ?? ''),
                              timestamp: new Date().toISOString(),
                            };
                            sendMessage('sessionEndedAt', payload);
                            notifications.show({
                              color: 'green',
                              title: 'Evento inviato',
                              message: 'sessionEndedAt',
                            });
                          }}
                          disabled={!deviceCode}
                        >
                          Stop sessione
                        </Button>
                        <Button
                          size='sm'
                          variant='light'
                          onClick={() => {
                            const payload = {
                              timestamp: Date.now(),
                              ...(deviceCode ? { clientId: deviceCode } : {}),
                            };
                            sendMessage('mealEnded', payload);
                            notifications.show({
                              color: 'green',
                              title: 'Evento inviato',
                              message: 'mealEnded',
                            });
                          }}
                          disabled={!deviceCode}
                        >
                          Pasto terminato
                        </Button>
                      </Group>
                      <Select
                        size='sm'
                        label='Item (per itemShown / itemFeedback)'
                        data={foodOptions}
                        value={exposureFoodId}
                        onChange={value => value && setExposureFoodId(value)}
                        style={{ maxWidth: 280 }}
                        comboboxProps={{ withinPortal: true, zIndex: 10_000 }}
                      />
                    </Stack>
                  </Card>
                </Tabs.Panel>
              )}
            </Tabs>
          </ScrollArea>
        </Collapse>
      </Card>
    </div>
  );
};

export default AblyDebugPanel;
